import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FlagIcon } from '@heroicons/react/24/outline';
import type { ApiEndpoints } from '@avisoma/shared';
import { Button } from '../../catalyst/button';
import { Dialog, DialogTitle, DialogDescription, DialogBody, DialogActions } from '../../catalyst/dialog';
import { Textarea } from '../../catalyst/textarea';
import { useToast } from '../../components/Toast';
import backend from '../../services/backend';

type CurationReviews = ApiEndpoints<string>['/api/document-pages/:id/curation-reviews']['GET']['response'];

export function FlagForCurationButton({
  pageId,
  pending,
  loading,
}: {
  pageId: string;
  pending: boolean;
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const toast = useToast();
  const queryClient = useQueryClient();
  const reviewsKey = ['pageCurationReviews', pageId];

  const flagMutation = useMutation({
    mutationFn: () => backend.flagDocumentPage(pageId, note.trim() || undefined),
    // Optimistically mark the page pending so the button disables at once. cancelQueries stops an
    // in-flight reviews GET (carrying the old hasPendingFlag=false) from clobbering the patch;
    // onError rolls back; onSettled refetches to confirm against the server. The button keys off
    // the server's hasPendingFlag alone — no local state to go stale — so it re-enables on its own
    // once a verdict resolves the flag.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: reviewsKey });
      const previous = queryClient.getQueryData<CurationReviews>(reviewsKey);
      queryClient.setQueryData<CurationReviews>(reviewsKey, (old) =>
        old ? { ...old, hasPendingFlag: true } : old,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(reviewsKey, ctx.previous);
      toast.error('Failed to flag page for curation');
    },
    onSuccess: () => {
      setOpen(false);
      setNote('');
      toast.success('Page flagged for curation review');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: reviewsKey });
    },
  });

  // Disabled purely on the server's pending-flag state (persists across reloads, clears on review).
  if (pending) {
    return (
      <Button outline disabled title="Already flagged — pending review">
        <FlagIcon />
        Flagged
      </Button>
    );
  }

  return (
    <>
      <Button outline onClick={() => setOpen(true)} disabled={loading}>
        <FlagIcon />
        Flag for review
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} size="md">
        <DialogTitle>Flag for curation</DialogTitle>
        <DialogDescription>
          This page will be added to the curation queue for manual review. Optionally add a note to describe what
          needs attention.
        </DialogDescription>
        <DialogBody>
          <Textarea
            rows={3}
            placeholder="Optional note (e.g. wrong category, extraction looks off)…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={flagMutation.isPending}
            resizable={false}
          />
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setOpen(false)} disabled={flagMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => flagMutation.mutate()} disabled={flagMutation.isPending}>
            {flagMutation.isPending ? 'Flagging…' : 'Flag for review'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
